import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { business } from '../mock';

const links = [
  { label: 'About', hash: '#about' },
  { label: 'Services', hash: '#services' },
  { label: 'How It Works', hash: '#how' },
  { label: 'Reviews', hash: '#reviews' },
  { label: 'Careers', to: '/careers' },
  { label: 'Contact', hash: '#contact' },
];

export default function Navbar({ onSchedule }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goHash = (hash) => {
    setOpen(false);
    const id = hash.replace('#', '');
    const scrollTo = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(scrollTo, 120);
    } else {
      scrollTo();
    }
  };

  const elevated = scrolled;

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="container-x pt-3 sm:pt-4">
        <div
          className="rounded-[22px] px-4 sm:px-6 transition-all duration-300"
          style={{
            backgroundColor: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(12px)',
            border: '1px solid var(--line)',
            boxShadow: elevated
              ? '0 18px 44px -22px rgba(30,59,44,0.38)'
              : '0 12px 34px -20px rgba(30,59,44,0.22)',
          }}
        >
          <div className="flex items-center justify-between h-[72px] sm:h-[76px] gap-4">
            <Link to="/" className="flex items-center shrink-0 mr-2" onClick={() => window.scrollTo({ top: 0 })} aria-label={`${business.name} ${business.nameLine2}`}>
              <img src="/brand-logo.png" alt={`${business.name} ${business.nameLine2}`} className="h-[54px] sm:h-[58px] w-auto" style={{ objectFit: 'contain' }} />
            </Link>

            <div className="hidden lg:flex items-center gap-7">
              <nav className="flex items-center gap-6">
                {links.map((l) => (
                  l.to ? (
                    <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="text-[15px] font-medium whitespace-nowrap transition-colors duration-200 hover:opacity-60" style={{ color: 'var(--ink)' }}>{l.label}</Link>
                  ) : (
                    <button key={l.label} onClick={() => goHash(l.hash)} className="text-[15px] font-medium whitespace-nowrap transition-colors duration-200 hover:opacity-60" style={{ color: 'var(--ink)' }}>{l.label}</button>
                  )
                ))}
              </nav>
              <div className="flex items-center gap-4 shrink-0">
                <a href={business.phoneHref} className="hidden xl:flex items-center gap-2 text-[15px] font-semibold whitespace-nowrap" style={{ color: 'var(--forest)' }}>
                  <Phone size={17} /> {business.phone}
                </a>
                <button className="btn-sage whitespace-nowrap" onClick={onSchedule} style={{ padding: '12px 20px' }} data-testid="nav-schedule-btn">Schedule a Consultation</button>
              </div>
            </div>

            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu" style={{ color: 'var(--forest)' }}>
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden mt-2 rounded-[22px] overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.98)', border: '1px solid var(--line)', boxShadow: '0 18px 44px -22px rgba(30,59,44,0.38)' }}>
            <div className="px-6 py-5 flex flex-col gap-4">
              {links.map((l) => (
                l.to ? (
                  <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="text-lg font-medium" style={{ color: 'var(--ink)' }}>{l.label}</Link>
                ) : (
                  <button key={l.label} onClick={() => goHash(l.hash)} className="text-lg font-medium text-left" style={{ color: 'var(--ink)' }}>{l.label}</button>
                )
              ))}
              <a href={business.phoneHref} className="flex items-center gap-2 font-semibold" style={{ color: 'var(--forest)' }}>
                <Phone size={18} /> {business.phone}
              </a>
              <button className="btn-sage justify-center" onClick={() => { setOpen(false); onSchedule(); }}>Schedule a Consultation</button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
